// src/components/lists/BeingsList/PetsList.tsx
import React from 'react';
import { Typography } from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  ContentCopy as CopyIcon,
  Person as PersonIcon,
  WarningAmberOutlined as WarningIcon,
  Pets as PetsBreedIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../../theme/theme';
import {
  BeingsListPrimaryButton,
  BeingsListSecondaryButton,
  BeingsListTertiaryButton,
} from '../../common/Buttons';
import CopyButton from '../../common/CopyButton';
import {
  ListContainer,
  ContactDetailsSection as BaseContactDetailsSection,
  BeingRow,
  RowClickableArea,
  BeingNameSection,
  BeingIcon,
  BeingName,
  SkeletonBeingName,
  ActionButtons,
  ContactSection,
  ContactItem,
  PetCountDisplay,
  isBeingDataLoaded,
  ItemWarningIcon,
  StillOwnedIcon,
} from './beingsListStyles';
import {
  useBeingsListState,
  useBeingsListUtils,
  getPetOwnerContactInfo,
  createKeyDownHandler,
  getAccessibilityProps,
} from './beingsListBase';
import { PetsListProps, ExtendedPet } from './beingsListTypes';
import { getCustomIcon } from '../../../utils/customIcons';

const EnhancedBeingRow = styled(BeingRow)<{
  isOpen?: boolean;
  isEven?: boolean;
}>`
  &:hover {
    .pet-count-display {
      opacity: ${(props) => (props.isOpen ? 1 : 0.8)} !important;
    }
  }
`;

const PetsContactDetailsSection = styled(BaseContactDetailsSection)({
  '@media (min-width: 600px)': {
    paddingLeft: '3rem',
    paddingRight: '3rem',
  },
});

interface PetsListWithFiltersProps extends PetsListProps {
  allPetsForSiblingCalc?: ExtendedPet[];
}

const PetsList: React.FC<PetsListWithFiltersProps> = ({
  items,
  allPetsForSiblingCalc,
  onEdit,
  onView,
  onDelete,
  onNewBooking,
  onViewBookings,
  onViewAdmin,
  onViewMedical,
  onOwnerClick,
  onPetClick,
  openItemId: openItemIdProp,
  onOpenChange,
}) => {
  // Controlled when openItemIdProp is defined (number | null);
  // uncontrolled when undefined (fall back to internal state).
  const isControlled = openItemIdProp !== undefined;
  const internal = useBeingsListState();
  const openItemId = isControlled
    ? (openItemIdProp ?? null)
    : internal.openItemId;

  const setOpenItemId = (id: number | null) => {
    if (isControlled) {
      onOpenChange?.(id);
    } else {
      internal.setOpenItemId(id);
      onOpenChange?.(id);
    }
  };

  const utils = useBeingsListUtils(openItemId, setOpenItemId);
  const navigate = useNavigate();

  const formatSiblingPetCount = (pet: ExtendedPet) => {
    const petsToSearch = allPetsForSiblingCalc || items;
    const clientPets = petsToSearch.filter(
      (p: ExtendedPet) => p.client?.id === pet.client?.id
    );

    if (!clientPets || clientPets.length <= 1) return '(Only pet)';

    const siblingPets = clientPets.filter(
      (siblingPet: ExtendedPet) => siblingPet.id !== pet.id
    );

    if (siblingPets.length === 0) return '(Only pet)';

    const petNames = siblingPets
      .map((p: ExtendedPet) => p.name?.trim())
      .filter((name: string | undefined) => !!name && name.length > 0);

    return petNames.length > 0
      ? `(${petNames.join(', ')})`
      : '(Unnamed siblings)';
  };

  return (
    <ListContainer>
      {items.map((pet: ExtendedPet, index: number) => {
        const itemId = pet.id;
        const isOpen = openItemId === itemId;
        const isEven = index % 2 === 0;
        const petName = utils.renderName(pet);
        const ownerContactInfo = getPetOwnerContactInfo(pet);
        const siblingPetCountText = formatSiblingPetCount(pet);
        const dataLoaded = isBeingDataLoaded(pet, 'pets');

        return (
          <EnhancedBeingRow
            key={itemId}
            isOpen={isOpen}
            isEven={isEven}
            {...getAccessibilityProps(isOpen, petName)}
            id={`pet-row-${itemId}`}
            onKeyDown={createKeyDownHandler(
              isOpen,
              itemId,
              utils.handleNameClick,
              utils.handleRowClick
            )}
          >
            <RowClickableArea
              isOpen={isOpen}
              onClick={() => utils.handleRowClick(itemId)}
              aria-hidden="true"
            />

            <BeingNameSection
              className="being-name-section"
              isOpen={isOpen}
              onClick={
                isOpen
                  ? (e: React.MouseEvent) => utils.handleNameClick(itemId, e)
                  : (e: React.MouseEvent) => {
                      e.stopPropagation();
                      utils.handleRowClick(itemId);
                    }
              }
            >
              <BeingIcon isOpen={isOpen}>
                {getCustomIcon(pet.petType?.type, '2rem')}
              </BeingIcon>
              {pet.showWarning && (
                <ItemWarningIcon isOpen={isOpen}>
                  <WarningIcon />
                </ItemWarningIcon>
              )}
              <StillOwnedIcon isOpen={isOpen}>
                {!pet.stillOwned && getCustomIcon('cross', '2rem')}
              </StillOwnedIcon>
              <BeingName isOpen={isOpen}>
                {dataLoaded ? (
                  <>
                    <span className="surname">{pet.name}</span>
                    {pet.client?.lastName && `, ${pet.client.lastName}`}
                    <span className="surname"> - {pet.petBreed?.breed}</span>
                    <PetCountDisplay
                      isOpen={isOpen}
                      className="pet-count-display"
                    >
                      {siblingPetCountText}
                    </PetCountDisplay>
                  </>
                ) : (
                  <SkeletonBeingName
                    isOpen={isOpen}
                    widthVariant="short"
                    animation="wave"
                  />
                )}
              </BeingName>
            </BeingNameSection>

            {isOpen && (
              <ActionButtons>
                <BeingsListSecondaryButton
                  minWidth="250px"
                  onClick={() => onNewBooking?.(itemId)}
                >
                  NEW BOOKING
                </BeingsListSecondaryButton>

                <BeingsListPrimaryButton
                  minWidth="200px"
                  onClick={() => onViewBookings?.(itemId)}
                >
                  BOOKINGS
                </BeingsListPrimaryButton>

                {pet.client?.id && (
                  <BeingsListPrimaryButton
                    minWidth="176px"
                    onClick={() =>
                      navigate(`/clients/${pet.client!.id}/account`)
                    }
                  >
                    ACCOUNT
                  </BeingsListPrimaryButton>
                )}

                {pet.client?.id && (
                  <BeingsListTertiaryButton
                    minWidth="154px"
                    onClick={() =>
                      navigate(`/clients/${pet.client!.id}/quotations`)
                    }
                  >
                    QUOTES
                  </BeingsListTertiaryButton>
                )}

                {pet.client?.id && (
                  <BeingsListTertiaryButton
                    minWidth="150px"
                    onClick={() => navigate(`/clients/${pet.client!.id}/pets`)}
                  >
                    PETS
                  </BeingsListTertiaryButton>
                )}

                <BeingsListTertiaryButton
                  minWidth="150px"
                  onClick={() => onView?.(itemId)}
                >
                  VIEW
                </BeingsListTertiaryButton>

                <BeingsListTertiaryButton
                  minWidth="150px"
                  onClick={() => onEdit?.(itemId)}
                >
                  EDIT
                </BeingsListTertiaryButton>
              </ActionButtons>
            )}

            <PetsContactDetailsSection isOpen={isOpen}>
              <ContactSection sx={{ paddingTop: '1rem' }}>
                {pet.showWarning && (
                  <ContactItem>
                    <WarningIcon />
                    <Typography variant="body1" fontWeight={600}>
                      {pet.notes}
                    </Typography>
                  </ContactItem>
                )}

                <ContactItem>
                  <PetsBreedIcon />
                  <Typography variant="body1" fontWeight={600}>
                    {pet.petBreed?.breed}
                  </Typography>
                </ContactItem>

                <ContactItem>
                  <PersonIcon
                    sx={{ fontSize: '1.4rem', color: colors.primary.main }}
                  />
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={() =>
                      pet.client?.id && onOwnerClick?.(pet.client.id)
                    }
                  >
                    {pet.client?.firstName} {pet.client?.lastName}
                  </Typography>
                </ContactItem>

                <ContactItem>
                  <PhoneIcon
                    sx={{ fontSize: '1.4rem', color: colors.primary.main }}
                  />
                  <Typography variant="body1" fontWeight={600}>
                    {ownerContactInfo.phone}
                  </Typography>
                  {ownerContactInfo.hasPhone && (
                    <CopyButton
                      valueToCopy={ownerContactInfo.phone}
                      icon={<CopyIcon fontSize="small" />}
                      size="small"
                      aria-label="Copy owner's phone number"
                    />
                  )}
                </ContactItem>

                <ContactItem>
                  <EmailIcon
                    sx={{ fontSize: '1.4rem', color: colors.primary.main }}
                  />
                  <Typography variant="body1" fontWeight={600}>
                    {ownerContactInfo.email}
                  </Typography>
                  {ownerContactInfo.hasEmail && (
                    <CopyButton
                      valueToCopy={ownerContactInfo.email}
                      icon={<CopyIcon fontSize="small" />}
                      size="small"
                      aria-label="Copy owner's email address"
                    />
                  )}
                </ContactItem>
              </ContactSection>
            </PetsContactDetailsSection>
          </EnhancedBeingRow>
        );
      })}
    </ListContainer>
  );
};

export default PetsList;
